/**
 * Arithmetic on in-world time (§9).
 *
 * Time is a point on a continuous minute line, not a turn counter. Every
 * action costs what it costs; nothing here knows about "slots" (§22). The
 * world does not tick — these helpers exist so the sim can ask "how long
 * since" at the moment it needs the answer (§10).
 */

import type { DayOfWeek, Minutes, WorldTime } from './types/index.js';

export const MINUTES_PER_HOUR = 60;
export const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

/** Total minutes since the start of day 0. Negative days are fine (§3). */
export function toMinutes(t: WorldTime): number {
  return t.day * MINUTES_PER_DAY + t.minute;
}

/** The inverse of `toMinutes`. Always yields `minute` in `0..1439`. */
export function fromMinutes(total: number): WorldTime {
  const day = Math.floor(total / MINUTES_PER_DAY);
  return { day, minute: total - day * MINUTES_PER_DAY };
}

/** A moment on a given day. `hour` is 0–23. */
export function timeOf(day: number, hour: number, minute = 0): WorldTime {
  return fromMinutes(day * MINUTES_PER_DAY + hour * MINUTES_PER_HOUR + minute);
}

/** Move a moment forward (or, with a negative argument, back). */
export function addMinutes(t: WorldTime, minutes: Minutes): WorldTime {
  return fromMinutes(toMinutes(t) + minutes);
}

/** Signed: positive when `to` is after `from`. */
export function minutesBetween(from: WorldTime, to: WorldTime): number {
  return toMinutes(to) - toMinutes(from);
}

/** Negative when `a` is earlier, zero when equal, positive when later. */
export function compareTime(a: WorldTime, b: WorldTime): number {
  return toMinutes(a) - toMinutes(b);
}

export function isBefore(a: WorldTime, b: WorldTime): boolean {
  return compareTime(a, b) < 0;
}

export function isSameOrAfter(a: WorldTime, b: WorldTime): boolean {
  return compareTime(a, b) >= 0;
}

/** True when the two moments fall on different calendar days. */
export function crossesDay(from: WorldTime, to: WorldTime): boolean {
  return from.day !== to.day;
}

/**
 * Day 0 is a Sunday and the week repeats from there, forward and back, so
 * the canon week at negative days (§3) also has weekdays. Routines (§15)
 * key on this.
 */
export function dayOfWeek(t: WorldTime | number): DayOfWeek {
  const day = typeof t === 'number' ? t : t.day;
  return ((((day % 7) + 7) % 7) as DayOfWeek);
}

/** A well-formed moment: integer day, integer minute inside the day. */
export function isValidTime(t: unknown): t is WorldTime {
  if (typeof t !== 'object' || t === null) return false;
  const { day, minute } = t as { day?: unknown; minute?: unknown };
  return (
    Number.isInteger(day) &&
    Number.isInteger(minute) &&
    (minute as number) >= 0 &&
    (minute as number) < MINUTES_PER_DAY
  );
}

/** A positive, whole number of minutes: the only cost an action may carry. */
export function isValidDuration(m: unknown): m is Minutes {
  return Number.isInteger(m) && (m as number) > 0;
}

/** "Day 3, 14:05" — for transcripts and debugging, never for the model. */
export function formatTime(t: WorldTime): string {
  const hour = Math.floor(t.minute / MINUTES_PER_HOUR);
  const minute = t.minute % MINUTES_PER_HOUR;
  const hh = String(hour).padStart(2, '0');
  const mm = String(minute).padStart(2, '0');
  return `Day ${t.day}, ${hh}:${mm}`;
}
